import type { UTCDate } from '../../utils/fechador'
import * as dt from '../../utils/fechador'
import { log } from '../../utils/logger'
import * as storage from '../../utils/storage'
import { STORAGE_KEYS } from '../modelo/storageKeys'
import { type FetchOptions, type TimeSeries } from './irsData'

// const EuriborCode = 'D_1NBAF472'

function mapAndFilter(data: any): TimeSeries {
    const idx = data.fechas.findIndex((f: string) => f.slice(0, 2) === '19')
    if (idx > -1) {
        data.fechas = data.fechas.slice(0, idx)
        data.valores = data.valores.slice(0, idx)
    }
    return {
        fechas: data.fechas.map((f: string) => dt.fromString(f)),
        valores: data.valores
    }
}

// https://app.bde.es/bierest/resources/srdatosapp/listaSeries?idioma=es&series=D_1NBAF472&rango=MAX
// const UrlEuribor = `https://app.bde.es/bierest/resources/srdatosapp/listaSeries?idioma=es&series=${EuriborCode}&rango=MAX`
const UrlEuribor = 'https://mercado.juan-turbera.workers.dev/api/euribor'

// --- 1. Cache lookup -------------------------------------------------

interface EuriborCacheHit {
    series: TimeSeries
    storageDate: UTCDate
    effectiveDate: UTCDate
}

function readEuriborCache(): EuriborCacheHit | null {
    const parsed = storage.getParsed(STORAGE_KEYS.euribor)
    if (!parsed) return null

    try {
        const storageDate = dt.fromString(parsed.date)
        const series = mapAndFilter(parsed.data)
        return { series, storageDate, effectiveDate: series.fechas[0] }

        // test
        // return null // for forced re-fetch from cloudflare
    } catch (err) {
        log.error('Euribor desde localStorage fallado (fetch de nuevo):', err)
        return null
    }
}

function isEuriborCacheStillValid(hit: EuriborCacheHit): boolean {
    return (
        dt.diffMonths(hit.effectiveDate, dt.firstNext()) <= 2 ||
        dt.eqDay(hit.storageDate, dt.todayUTC())
    )
}

// --- 2. Network / debug source ---------------------------------------

async function loadEuriborSource(options: FetchOptions): Promise<any[] | null> {
    if (options.debug) return euriborRaw

    try {
        const res = await fetch(UrlEuribor, { signal: AbortSignal.timeout(3000) })

        if (!res.ok) {
            log.warn('Not OK, fetch Euribor status:', res.status)
            return null
        }

        // log.info(JSON.stringify(Object.fromEntries(res.headers)))
        log.info(
            'Fetch Euribor from api:',
            res.headers?.get('X-Cache') ?? 'X-Cache header not found'
        )

        const jsonArray = await res.json()
        log.info('Euribor fetched del API con éxito.')
        return jsonArray
    } catch (err: any) {
        if (err.name === 'TimeoutError') {
            log.error('Fetch Euribor cancelado: La API tardó más de 3 segundos en responder.')
        } else if (err instanceof SyntaxError) {
            log.error('JSON Euribor inválido:', err)
        } else {
            log.error('Fetch Euribor falló por error de red o de sistema:', err)
        }
        return null
    }
}

// --- 3. Mapping (isolated) --------------------------------------------

function safeMapEuribor(jsonArray: any[]): TimeSeries | null {
    try {
        return mapAndFilter(jsonArray[0])
    } catch (err) {
        log.error('Mapping Euribor fallado:', err)
        return null
    }
}

// --- 4. Persist + log --------------------------------------------------

function persistEuribor(series: TimeSeries, options: FetchOptions): void {
    storage.setJson(STORAGE_KEYS.euribor, series)

    const mode = options.debug ? 'DEBUG' : 'LIFE'
    const effectiveDate = series.fechas[0]
    const noFechas = series.fechas.length
    const label = options.debug ? 'RAW_FAKE' : 'FETCH_BDE'
    const level = options.debug ? log.debug : log.info
    level(`Euribor ${mode} ${label} efectivo:${dt.toYMD(effectiveDate)} #fechas:${noFechas}`)
}

// --- Orchestrator --------------------------------------------------------

export async function fetchEuribor(options: FetchOptions): Promise<TimeSeries> {
    const mode = options.debug ? 'DEBUG' : 'LIFE'
    const EMPTY: TimeSeries = { fechas: [], valores: [] }

    const cached = readEuriborCache()
    if (cached && isEuriborCacheStillValid(cached)) {
        const noFechas = cached.series.fechas.length
        log.debug(
            `Euribor ${mode} localStorage:${dt.toYMD(cached.storageDate)} efectivo:${dt.toYMD(cached.effectiveDate)} #fechas:${noFechas}`
        )
        return cached.series
    }

    const jsonArray = await loadEuriborSource(options)
    if (!jsonArray) return EMPTY

    const series = safeMapEuribor(jsonArray)
    if (!series) return EMPTY

    persistEuribor(series, options)
    return series
}

// export async function fetchEuribor(options: FetchOptions): Promise<TimeSeries> {
//     const mode = options.debug ? 'DEBUG' : 'LIFE'
//     // 1. check localStorage
//     const parsed = storage.getParsed(STORAGE_KEYS.euribor)
//     if (parsed) {
//         try {
//             const storageDate = dt.fromString(parsed.date)
//             const euriborSeries: TimeSeries = mapAndFilter(parsed.data)
//             const effectiveDate = euriborSeries.fechas[0]
//             const noFechas = euriborSeries.fechas.length
//             if (dt.diffMonths(effectiveDate, dt.firstNext()) <= 2) {
//                 log.debug(
//                     `Euribor ${mode} localStorage:${dt.toYMD(storageDate)} efectivo:${dt.toYMD(effectiveDate)} #fechas:${noFechas}`
//                 )
//                 return euriborSeries
//             }
//             if (dt.eqDay(storageDate, dt.todayUTC())) {
//                 log.debug(
//                     `Euribor ${mode} localStorage TODAY efectivo:${dt.toYMD(effectiveDate)} #fechas:${noFechas}`
//                 )
//                 return euriborSeries
//             }
//         } catch (err) {
//             // si está corrupto, ignoramos y hacemos fetch
//             log.error('Euribor desde localStorage fallado (fetch de nuevo):', err)
//         }
//     }

//     // 2. fetch desde servidor (solo errores de red)
//     let jsonArray: any[]
//     try {
//         if (options.debug) {
//             jsonArray = euriborRaw
//         } else {
//             const res = await fetch(UrlEuribor, {
//                 signal: AbortSignal.timeout(3000)
//             })

//             if (!res.ok) {
//                 log.warn('Not OK, fetch Euribor status:', res.status)
//                 return { fechas: [], valores: [] }
//             }

//             // parse JSON aislado
//             try {
//                 jsonArray = await res.json()
//                 log.info('Euribor fetched del API con éxito.')
//             } catch (err) {
//                 log.error('JSON Euribor inválido:', err)
//                 return { fechas: [], valores: [] }
//             }
//         }
//     } catch (err: any) {
//         // log.error('Fetch Euribor fallado:', err)
//         // OUTER CATCH: Distinguir Timeout de Error de Red
//         if (err.name === 'TimeoutError') {
//             log.error('Fetch Euribor cancelado: La API tardó más de 3 segundos en responder.')
//         } else {
//             log.error('Fetch Euribor falló por error de red o de sistema:', err)
//         }
//         return { fechas: [], valores: [] }
//     }

//     // 3. map aislado
//     let euriborSeries: TimeSeries
//     try {
//         const jsonOject = jsonArray[0]
//         euriborSeries = mapAndFilter(jsonOject)
//     } catch (err) {
//         log.error('Mapping Euribor fallado:', err)
//         return { fechas: [], valores: [] }
//     }

//     storage.setJson(STORAGE_KEYS.euribor, euriborSeries)

//     const effectiveDate = euriborSeries.fechas[0]
//     const noFechas = euriborSeries.fechas.length
//     if (options.debug)
//         log.debug(
//             `Euribor ${mode} RAW_FAKE efectivo:${dt.toYMD(effectiveDate)} #fechas:${noFechas}`
//         )
//     else
//         log.info(
//             `Euribor ${mode} FETCH_BDE efectivo:${dt.toYMD(effectiveDate)} #fechas:${noFechas}`
//         )

//     return euriborSeries
// }

// ****************************************************************************
// test-data ******************************************************************
// ****************************************************************************
const euriborRaw = [
    {
        serie: 'D_1NBAF472',
        descripcion: 'Euribor a un año',
        descripcionCorta: 'Euribor a un año',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion: 'Euribor a un año'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'Tipos de interés. De referencia del mercado hipotecario. Euribor a un año.'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '332'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Ene 1999] 3,062 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 2,954 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Ene 2021] -0,505 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Jul 2008] 5,393 %'
            },
            {
                titulo: 'Fuente',
                descripcion:
                    'EURIBOR European Banking Federation\nACI Asociación de Mercados Financieros\nBanco Central Europeo'
            },
            {
                titulo: 'Notas',
                descripcion:
                    'Tipo de oferta de los depósitos interbancarios en el área del euro correspondientes a 57 de los bancos más activos del área.\nHasta diciembre de 1998 tipo sintético calculado ponderando los tipos nacionales por el Producto Interior Bruto.'
            },
            {
                titulo: 'Series relacionadas (cuadro pdf)',
                descripcion:
                    "<a href='https://www.bde.es/webbe/es/estadisticas/compartido/datos/pdf/a1901.pdf'>Descarga el cuadro en formato pdf con series relacionadas</a>"
            },
            {
                titulo: 'Series relacionadas (archivo Excel)',
                descripcion:
                    "<a href='https://www.bde.es/webbe/es/estadisticas/compartido/datos/xlsx/be1901.xlsx'>Descarga el archivo Excel con series relacionadas</a>"
            }
        ],
        fechaInicio: '1999-01-01T09:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z',
            '2023-10-01T08:15:00Z',
            '2023-09-01T08:15:00Z',
            '2023-08-01T08:15:00Z',
            '2023-07-01T08:15:00Z',
            '2023-06-01T08:15:00Z',
            '2023-05-01T08:15:00Z',
            '2023-04-01T08:15:00Z',
            '2023-03-01T09:15:00Z',
            '2023-02-01T09:15:00Z',
            '2023-01-01T09:15:00Z',
            '2022-12-01T09:15:00Z',
            '2022-11-01T09:15:00Z',
            '2022-10-01T08:15:00Z',
            '2022-09-01T08:15:00Z',
            '2022-08-01T08:15:00Z',
            '2022-07-01T08:15:00Z',
            '2022-06-01T08:15:00Z',
            '2022-05-01T08:15:00Z',
            '2022-04-01T08:15:00Z',
            '2022-03-01T09:15:00Z',
            '2022-02-01T09:15:00Z',
            '2022-01-01T09:15:00Z',
            '2021-12-01T09:15:00Z',
            '2021-11-01T09:15:00Z',
            '2021-10-01T08:15:00Z',
            '2021-09-01T08:15:00Z',
            '2021-08-01T08:15:00Z',
            '2021-07-01T08:15:00Z',
            '2021-06-01T08:15:00Z',
            '2021-05-01T08:15:00Z',
            '2021-04-01T08:15:00Z',
            '2021-03-01T09:15:00Z',
            '2021-02-01T09:15:00Z',
            '2021-01-01T09:15:00Z',
            '2020-12-01T09:15:00Z',
            '2020-11-01T09:15:00Z',
            '2020-10-01T08:15:00Z',
            '2020-09-01T08:15:00Z',
            '2020-08-01T08:15:00Z',
            '2020-07-01T08:15:00Z',
            '2020-06-01T08:15:00Z',
            '2020-05-01T08:15:00Z',
            '2020-04-01T08:15:00Z',
            '2020-03-01T09:15:00Z',
            '2020-02-01T09:15:00Z',
            '2020-01-01T09:15:00Z',
            '2019-12-01T09:15:00Z',
            '2019-11-01T09:15:00Z',
            '2019-10-01T08:15:00Z',
            '2019-09-01T08:15:00Z',
            '2019-08-01T08:15:00Z',
            '2019-07-01T08:15:00Z',
            '2019-06-01T08:15:00Z',
            '2019-05-01T08:15:00Z',
            '2019-04-01T08:15:00Z',
            '2019-03-01T09:15:00Z',
            '2019-02-01T09:15:00Z',
            '2019-01-01T09:15:00Z',
            '2018-12-01T09:15:00Z',
            '2018-11-01T09:15:00Z',
            '2018-10-01T08:15:00Z',
            '2018-09-01T08:15:00Z',
            '2018-08-01T08:15:00Z',
            '2018-07-01T08:15:00Z',
            '2018-06-01T08:15:00Z',
            '2018-05-01T08:15:00Z',
            '2018-04-01T08:15:00Z',
            '2018-03-01T09:15:00Z',
            '2018-02-01T09:15:00Z',
            '2018-01-01T09:15:00Z',
            '2017-12-01T09:15:00Z',
            '2017-11-01T09:15:00Z',
            '2017-10-01T08:15:00Z',
            '2017-09-01T08:15:00Z',
            '2017-08-01T08:15:00Z',
            '2017-07-01T08:15:00Z',
            '2017-06-01T08:15:00Z',
            '2017-05-01T08:15:00Z',
            '2017-04-01T08:15:00Z',
            '2017-03-01T09:15:00Z',
            '2017-02-01T09:15:00Z',
            '2017-01-01T09:15:00Z',
            '2016-12-01T09:15:00Z',
            '2016-11-01T09:15:00Z',
            '2016-10-01T08:15:00Z',
            '2016-09-01T08:15:00Z',
            '2016-08-01T08:15:00Z',
            '2016-07-01T08:15:00Z',
            '2016-06-01T08:15:00Z',
            '2016-05-01T08:15:00Z',
            '2016-04-01T08:15:00Z',
            '2016-03-01T09:15:00Z',
            '2016-02-01T09:15:00Z',
            '2016-01-01T09:15:00Z',
            '2015-12-01T09:15:00Z',
            '2015-11-01T09:15:00Z',
            '2015-10-01T08:15:00Z',
            '2015-09-01T08:15:00Z',
            '2015-08-01T08:15:00Z',
            '2015-07-01T08:15:00Z',
            '2015-06-01T08:15:00Z',
            '2015-05-01T08:15:00Z',
            '2015-04-01T08:15:00Z',
            '2015-03-01T09:15:00Z',
            '2015-02-01T09:15:00Z',
            '2015-01-01T09:15:00Z',
            '2014-12-01T09:15:00Z',
            '2014-11-01T09:15:00Z',
            '2014-10-01T08:15:00Z',
            '2014-09-01T08:15:00Z',
            '2014-08-01T08:15:00Z',
            '2014-07-01T08:15:00Z',
            '2014-06-01T08:15:00Z',
            '2014-05-01T08:15:00Z',
            '2014-04-01T08:15:00Z',
            '2014-03-01T09:15:00Z',
            '2014-02-01T09:15:00Z',
            '2014-01-01T09:15:00Z',
            '2013-12-01T09:15:00Z',
            '2013-11-01T09:15:00Z',
            '2013-10-01T08:15:00Z',
            '2013-09-01T08:15:00Z',
            '2013-08-01T08:15:00Z',
            '2013-07-01T08:15:00Z',
            '2013-06-01T08:15:00Z',
            '2013-05-01T08:15:00Z',
            '2013-04-01T08:15:00Z',
            '2013-03-01T09:15:00Z',
            '2013-02-01T09:15:00Z',
            '2013-01-01T09:15:00Z',
            '2012-12-01T09:15:00Z',
            '2012-11-01T09:15:00Z',
            '2012-10-01T08:15:00Z',
            '2012-09-01T08:15:00Z',
            '2012-08-01T08:15:00Z',
            '2012-07-01T08:15:00Z',
            '2012-06-01T08:15:00Z',
            '2012-05-01T08:15:00Z',
            '2012-04-01T08:15:00Z',
            '2012-03-01T09:15:00Z',
            '2012-02-01T09:15:00Z',
            '2012-01-01T09:15:00Z',
            '2011-12-01T09:15:00Z',
            '2011-11-01T09:15:00Z',
            '2011-10-01T08:15:00Z',
            '2011-09-01T08:15:00Z',
            '2011-08-01T08:15:00Z',
            '2011-07-01T08:15:00Z',
            '2011-06-01T08:15:00Z',
            '2011-05-01T08:15:00Z',
            '2011-04-01T08:15:00Z',
            '2011-03-01T09:15:00Z',
            '2011-02-01T09:15:00Z',
            '2011-01-01T09:15:00Z',
            '2010-12-01T09:15:00Z',
            '2010-11-01T09:15:00Z',
            '2010-10-01T08:15:00Z',
            '2010-09-01T08:15:00Z',
            '2010-08-01T08:15:00Z',
            '2010-07-01T08:15:00Z',
            '2010-06-01T08:15:00Z',
            '2010-05-01T08:15:00Z',
            '2010-04-01T08:15:00Z',
            '2010-03-01T09:15:00Z',
            '2010-02-01T09:15:00Z',
            '2010-01-01T09:15:00Z',
            '2009-12-01T09:15:00Z',
            '2009-11-01T09:15:00Z',
            '2009-10-01T08:15:00Z',
            '2009-09-01T08:15:00Z',
            '2009-08-01T08:15:00Z',
            '2009-07-01T08:15:00Z',
            '2009-06-01T08:15:00Z',
            '2009-05-01T08:15:00Z',
            '2009-04-01T08:15:00Z',
            '2009-03-01T09:15:00Z',
            '2009-02-01T09:15:00Z',
            '2009-01-01T09:15:00Z',
            '2008-12-01T09:15:00Z',
            '2008-11-01T09:15:00Z',
            '2008-10-01T08:15:00Z',
            '2008-09-01T08:15:00Z',
            '2008-08-01T08:15:00Z',
            '2008-07-01T08:15:00Z',
            '2008-06-01T08:15:00Z',
            '2008-05-01T08:15:00Z',
            '2008-04-01T08:15:00Z',
            '2008-03-01T09:15:00Z',
            '2008-02-01T09:15:00Z',
            '2008-01-01T09:15:00Z',
            '2007-12-01T09:15:00Z',
            '2007-11-01T09:15:00Z',
            '2007-10-01T08:15:00Z',
            '2007-09-01T08:15:00Z',
            '2007-08-01T08:15:00Z',
            '2007-07-01T08:15:00Z',
            '2007-06-01T08:15:00Z',
            '2007-05-01T08:15:00Z',
            '2007-04-01T08:15:00Z',
            '2007-03-01T09:15:00Z',
            '2007-02-01T09:15:00Z',
            '2007-01-01T09:15:00Z',
            '2006-12-01T09:15:00Z',
            '2006-11-01T09:15:00Z',
            '2006-10-01T08:15:00Z',
            '2006-09-01T08:15:00Z',
            '2006-08-01T08:15:00Z',
            '2006-07-01T08:15:00Z',
            '2006-06-01T08:15:00Z',
            '2006-05-01T08:15:00Z',
            '2006-04-01T08:15:00Z',
            '2006-03-01T09:15:00Z',
            '2006-02-01T09:15:00Z',
            '2006-01-01T09:15:00Z',
            '2005-12-01T09:15:00Z',
            '2005-11-01T09:15:00Z',
            '2005-10-01T08:15:00Z',
            '2005-09-01T08:15:00Z',
            '2005-08-01T08:15:00Z',
            '2005-07-01T08:15:00Z',
            '2005-06-01T08:15:00Z',
            '2005-05-01T08:15:00Z',
            '2005-04-01T08:15:00Z',
            '2005-03-01T09:15:00Z',
            '2005-02-01T09:15:00Z',
            '2005-01-01T09:15:00Z',
            '2004-12-01T09:15:00Z',
            '2004-11-01T09:15:00Z',
            '2004-10-01T08:15:00Z',
            '2004-09-01T08:15:00Z',
            '2004-08-01T08:15:00Z',
            '2004-07-01T08:15:00Z',
            '2004-06-01T08:15:00Z',
            '2004-05-01T08:15:00Z',
            '2004-04-01T08:15:00Z',
            '2004-03-01T09:15:00Z',
            '2004-02-01T09:15:00Z',
            '2004-01-01T09:15:00Z',
            '2003-12-01T09:15:00Z',
            '2003-11-01T09:15:00Z',
            '2003-10-01T08:15:00Z',
            '2003-09-01T08:15:00Z',
            '2003-08-01T08:15:00Z',
            '2003-07-01T08:15:00Z',
            '2003-06-01T08:15:00Z',
            '2003-05-01T08:15:00Z',
            '2003-04-01T08:15:00Z',
            '2003-03-01T09:15:00Z',
            '2003-02-01T09:15:00Z',
            '2003-01-01T09:15:00Z',
            '2002-12-01T09:15:00Z',
            '2002-11-01T09:15:00Z',
            '2002-10-01T08:15:00Z',
            '2002-09-01T08:15:00Z',
            '2002-08-01T08:15:00Z',
            '2002-07-01T08:15:00Z',
            '2002-06-01T08:15:00Z',
            '2002-05-01T08:15:00Z',
            '2002-04-01T08:15:00Z',
            '2002-03-01T09:15:00Z',
            '2002-02-01T09:15:00Z',
            '2002-01-01T09:15:00Z',
            '2001-12-01T09:15:00Z',
            '2001-11-01T09:15:00Z',
            '2001-10-01T08:15:00Z',
            '2001-09-01T08:15:00Z',
            '2001-08-01T08:15:00Z',
            '2001-07-01T08:15:00Z',
            '2001-06-01T08:15:00Z',
            '2001-05-01T08:15:00Z',
            '2001-04-01T08:15:00Z',
            '2001-03-01T09:15:00Z',
            '2001-02-01T09:15:00Z',
            '2001-01-01T09:15:00Z',
            '2000-12-01T09:15:00Z',
            '2000-11-01T09:15:00Z',
            '2000-10-01T08:15:00Z',
            '2000-09-01T08:15:00Z',
            '2000-08-01T08:15:00Z',
            '2000-07-01T08:15:00Z',
            '2000-06-01T08:15:00Z',
            '2000-05-01T08:15:00Z',
            '2000-04-01T08:15:00Z',
            '2000-03-01T09:15:00Z',
            '2000-02-01T09:15:00Z',
            '2000-01-01T09:15:00Z',
            '1999-12-01T09:15:00Z',
            '1999-11-01T09:15:00Z',
            '1999-10-01T08:15:00Z',
            '1999-09-01T08:15:00Z',
            '1999-08-01T08:15:00Z',
            '1999-07-01T08:15:00Z',
            '1999-06-01T08:15:00Z',
            '1999-05-01T08:15:00Z',
            '1999-04-01T08:15:00Z',
            '1999-03-01T09:15:00Z',
            '1999-02-01T09:15:00Z',
            '1999-01-01T09:15:00Z'
        ],
        valores: [
            2.954, 2.855, 2.798, 2.804, 2.747, 2.565, 2.221, 2.245, 2.267, 2.217, 2.187, 2.172,
            2.114, 2.079, 2.081, 2.081, 2.143, 2.398, 2.407, 2.525, 2.436, 2.506, 2.691, 2.936,
            3.166, 3.526, 3.65, 3.68, 3.703, 3.718, 3.671, 3.609, 3.679, 4.022, 4.16, 4.149, 4.073,
            4.149, 4.007, 3.862, 3.757, 3.647, 3.534, 3.337, 3.018, 2.828, 2.629, 2.233, 1.249,
            0.992, 0.852, 0.287, 0.013, -0.237, -0.335, -0.477, -0.502, -0.487, -0.477, -0.492,
            -0.498, -0.491, -0.484, -0.481, -0.484, -0.487, -0.501, -0.505, -0.497, -0.481, -0.466,
            -0.415, -0.359, -0.279, -0.147, -0.081, -0.108, -0.266, -0.288, -0.253, -0.261, -0.272,
            -0.304, -0.339, -0.356, -0.283, -0.19, -0.134, -0.112, -0.109, -0.108, -0.116, -0.129,
            -0.147, -0.154, -0.166, -0.169, -0.18, -0.181, -0.188, -0.19, -0.191, -0.191, -0.189,
            -0.19, -0.189, -0.18, -0.168, -0.156, -0.154, -0.149, -0.127, -0.119, -0.11, -0.106,
            -0.095, -0.08, -0.074, -0.069, -0.057, -0.048, -0.056, -0.028, -0.013, -0.01, -0.012,
            -0.008, 0.042, 0.059, 0.079, 0.128, 0.154, 0.161, 0.167, 0.163, 0.165, 0.18, 0.212,
            0.255, 0.298, 0.329, 0.335, 0.338, 0.362, 0.469, 0.488, 0.513, 0.592, 0.604, 0.577,
            0.549, 0.562, 0.543, 0.506, 0.541, 0.543, 0.542, 0.525, 0.507, 0.484, 0.528, 0.545,
            0.594, 0.575, 0.549, 0.588, 0.65, 0.74, 0.877, 1.061, 1.219, 1.266, 1.368, 1.499, 1.678,
            1.837, 2.004, 2.044, 2.11, 2.067, 2.097, 2.183, 2.144, 2.147, 2.086, 1.924, 1.714, 1.55,
            1.526, 1.54, 1.495, 1.42, 1.421, 1.373, 1.281, 1.249, 1.225, 1.215, 1.225, 1.232, 1.242,
            1.231, 1.243, 1.261, 1.334, 1.412, 1.61, 1.644, 1.771, 1.909, 2.135, 2.622, 3.452, 4.35,
            5.248, 5.384, 5.323, 5.393, 5.361, 4.994, 4.82, 4.59, 4.349, 4.498, 4.793, 4.607, 4.647,
            4.724, 4.666, 4.564, 4.505, 4.373, 4.253, 4.106, 4.094, 4.064, 3.921, 3.864, 3.799,
            3.715, 3.615, 3.539, 3.401, 3.308, 3.221, 3.105, 2.914, 2.833, 2.783, 2.684, 2.414,
            2.219, 2.223, 2.168, 2.103, 2.193, 2.265, 2.335, 2.31, 2.312, 2.301, 2.328, 2.316,
            2.377, 2.302, 2.361, 2.404, 2.297, 2.163, 2.055, 2.163, 2.216, 2.381, 2.41, 2.303,
            2.258, 2.279, 2.076, 2.014, 2.252, 2.447, 2.411, 2.504, 2.705, 2.873, 3.017, 3.126,
            3.236, 3.44, 3.645, 3.869, 3.963, 3.86, 3.816, 3.594, 3.483, 3.298, 3.198, 3.369, 3.77,
            4.108, 4.311, 4.312, 4.52, 4.481, 4.471, 4.591, 4.574, 4.881, 5.193, 5.218, 5.219,
            5.248, 5.105, 4.965, 4.849, 4.365, 4.267, 4.111, 3.949, 3.826, 3.689, 3.684, 3.301,
            3.237, 3.03, 2.836, 2.683, 2.756, 3.046, 3.03, 3.062
        ]
    }
]

const euriborRaw_20260501 = [
    {
        serie: 'D_1NBAF472',
        descripcion: 'Euribor a un año',
        descripcionCorta: 'Euribor a un año',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion: 'Euribor a un año'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'Tipos de interés. De referencia del mercado hipotecario. Euribor a un año.'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '329'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Ene 1999] 3,062 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,804 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Ene 2021] -0,505 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Jul 2008] 5,393 %'
            },
            {
                titulo: 'Fuente',
                descripcion:
                    'EURIBOR European Banking Federation\nACI Asociación de Mercados Financieros\nBanco Central Europeo'
            },
            {
                titulo: 'Notas',
                descripcion:
                    'Tipo de oferta de los depósitos interbancarios en el área del euro correspondientes a 57 de los bancos más activos del área.\nHasta diciembre de 1998 tipo sintético calculado ponderando los tipos nacionales por el Producto Interior Bruto.'
            },
            {
                titulo: 'Series relacionadas (cuadro pdf)',
                descripcion:
                    "<a href='https://www.bde.es/webbe/es/estadisticas/compartido/datos/pdf/a1901.pdf'>Descarga el cuadro en formato pdf con series relacionadas</a>"
            },
            {
                titulo: 'Series relacionadas (archivo Excel)',
                descripcion:
                    "<a href='https://www.bde.es/webbe/es/estadisticas/compartido/datos/xlsx/be1901.xlsx'>Descarga el archivo Excel con series relacionadas</a>"
            }
        ],
        fechaInicio: '1999-01-01T09:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z',
            '2023-10-01T08:15:00Z',
            '2023-09-01T08:15:00Z',
            '2023-08-01T08:15:00Z',
            '2023-07-01T08:15:00Z',
            '2023-06-01T08:15:00Z',
            '2023-05-01T08:15:00Z',
            '2023-04-01T08:15:00Z',
            '2023-03-01T09:15:00Z',
            '2023-02-01T09:15:00Z',
            '2023-01-01T09:15:00Z',
            '2022-12-01T09:15:00Z',
            '2022-11-01T09:15:00Z',
            '2022-10-01T08:15:00Z',
            '2022-09-01T08:15:00Z',
            '2022-08-01T08:15:00Z',
            '2022-07-01T08:15:00Z',
            '2022-06-01T08:15:00Z',
            '2022-05-01T08:15:00Z',
            '2022-04-01T08:15:00Z',
            '2022-03-01T09:15:00Z',
            '2022-02-01T09:15:00Z',
            '2022-01-01T09:15:00Z',
            '2021-12-01T09:15:00Z',
            '2021-11-01T09:15:00Z',
            '2021-10-01T08:15:00Z',
            '2021-09-01T08:15:00Z',
            '2021-08-01T08:15:00Z',
            '2021-07-01T08:15:00Z',
            '2021-06-01T08:15:00Z',
            '2021-05-01T08:15:00Z',
            '2021-04-01T08:15:00Z',
            '2021-03-01T09:15:00Z',
            '2021-02-01T09:15:00Z',
            '2021-01-01T09:15:00Z',
            '2020-12-01T09:15:00Z',
            '2020-11-01T09:15:00Z',
            '2020-10-01T08:15:00Z',
            '2020-09-01T08:15:00Z',
            '2020-08-01T08:15:00Z',
            '2020-07-01T08:15:00Z',
            '2020-06-01T08:15:00Z',
            '2020-05-01T08:15:00Z',
            '2020-04-01T08:15:00Z',
            '2020-03-01T09:15:00Z',
            '2020-02-01T09:15:00Z',
            '2020-01-01T09:15:00Z',
            '2019-12-01T09:15:00Z',
            '2019-11-01T09:15:00Z',
            '2019-10-01T08:15:00Z',
            '2019-09-01T08:15:00Z',
            '2019-08-01T08:15:00Z',
            '2019-07-01T08:15:00Z',
            '2019-06-01T08:15:00Z',
            '2019-05-01T08:15:00Z',
            '2019-04-01T08:15:00Z',
            '2019-03-01T09:15:00Z',
            '2019-02-01T09:15:00Z',
            '2019-01-01T09:15:00Z',
            '2018-12-01T09:15:00Z',
            '2018-11-01T09:15:00Z',
            '2018-10-01T08:15:00Z',
            '2018-09-01T08:15:00Z',
            '2018-08-01T08:15:00Z',
            '2018-07-01T08:15:00Z',
            '2018-06-01T08:15:00Z',
            '2018-05-01T08:15:00Z',
            '2018-04-01T08:15:00Z',
            '2018-03-01T09:15:00Z',
            '2018-02-01T09:15:00Z',
            '2018-01-01T09:15:00Z',
            '2017-12-01T09:15:00Z',
            '2017-11-01T09:15:00Z',
            '2017-10-01T08:15:00Z',
            '2017-09-01T08:15:00Z',
            '2017-08-01T08:15:00Z',
            '2017-07-01T08:15:00Z',
            '2017-06-01T08:15:00Z',
            '2017-05-01T08:15:00Z',
            '2017-04-01T08:15:00Z',
            '2017-03-01T09:15:00Z',
            '2017-02-01T09:15:00Z',
            '2017-01-01T09:15:00Z',
            '2016-12-01T09:15:00Z',
            '2016-11-01T09:15:00Z',
            '2016-10-01T08:15:00Z',
            '2016-09-01T08:15:00Z',
            '2016-08-01T08:15:00Z',
            '2016-07-01T08:15:00Z',
            '2016-06-01T08:15:00Z',
            '2016-05-01T08:15:00Z',
            '2016-04-01T08:15:00Z',
            '2016-03-01T09:15:00Z',
            '2016-02-01T09:15:00Z',
            '2016-01-01T09:15:00Z',
            '2015-12-01T09:15:00Z',
            '2015-11-01T09:15:00Z',
            '2015-10-01T08:15:00Z',
            '2015-09-01T08:15:00Z',
            '2015-08-01T08:15:00Z',
            '2015-07-01T08:15:00Z',
            '2015-06-01T08:15:00Z',
            '2015-05-01T08:15:00Z',
            '2015-04-01T08:15:00Z',
            '2015-03-01T09:15:00Z',
            '2015-02-01T09:15:00Z',
            '2015-01-01T09:15:00Z',
            '2014-12-01T09:15:00Z',
            '2014-11-01T09:15:00Z',
            '2014-10-01T08:15:00Z',
            '2014-09-01T08:15:00Z',
            '2014-08-01T08:15:00Z',
            '2014-07-01T08:15:00Z',
            '2014-06-01T08:15:00Z',
            '2014-05-01T08:15:00Z',
            '2014-04-01T08:15:00Z',
            '2014-03-01T09:15:00Z',
            '2014-02-01T09:15:00Z',
            '2014-01-01T09:15:00Z',
            '2013-12-01T09:15:00Z',
            '2013-11-01T09:15:00Z',
            '2013-10-01T08:15:00Z',
            '2013-09-01T08:15:00Z',
            '2013-08-01T08:15:00Z',
            '2013-07-01T08:15:00Z',
            '2013-06-01T08:15:00Z',
            '2013-05-01T08:15:00Z',
            '2013-04-01T08:15:00Z',
            '2013-03-01T09:15:00Z',
            '2013-02-01T09:15:00Z',
            '2013-01-01T09:15:00Z',
            '2012-12-01T09:15:00Z',
            '2012-11-01T09:15:00Z',
            '2012-10-01T08:15:00Z',
            '2012-09-01T08:15:00Z',
            '2012-08-01T08:15:00Z',
            '2012-07-01T08:15:00Z',
            '2012-06-01T08:15:00Z',
            '2012-05-01T08:15:00Z',
            '2012-04-01T08:15:00Z',
            '2012-03-01T09:15:00Z',
            '2012-02-01T09:15:00Z',
            '2012-01-01T09:15:00Z',
            '2011-12-01T09:15:00Z',
            '2011-11-01T09:15:00Z',
            '2011-10-01T08:15:00Z',
            '2011-09-01T08:15:00Z',
            '2011-08-01T08:15:00Z',
            '2011-07-01T08:15:00Z',
            '2011-06-01T08:15:00Z',
            '2011-05-01T08:15:00Z',
            '2011-04-01T08:15:00Z',
            '2011-03-01T09:15:00Z',
            '2011-02-01T09:15:00Z',
            '2011-01-01T09:15:00Z',
            '2010-12-01T09:15:00Z',
            '2010-11-01T09:15:00Z',
            '2010-10-01T08:15:00Z',
            '2010-09-01T08:15:00Z',
            '2010-08-01T08:15:00Z',
            '2010-07-01T08:15:00Z',
            '2010-06-01T08:15:00Z',
            '2010-05-01T08:15:00Z',
            '2010-04-01T08:15:00Z',
            '2010-03-01T09:15:00Z',
            '2010-02-01T09:15:00Z',
            '2010-01-01T09:15:00Z',
            '2009-12-01T09:15:00Z',
            '2009-11-01T09:15:00Z',
            '2009-10-01T08:15:00Z',
            '2009-09-01T08:15:00Z',
            '2009-08-01T08:15:00Z',
            '2009-07-01T08:15:00Z',
            '2009-06-01T08:15:00Z',
            '2009-05-01T08:15:00Z',
            '2009-04-01T08:15:00Z',
            '2009-03-01T09:15:00Z',
            '2009-02-01T09:15:00Z',
            '2009-01-01T09:15:00Z',
            '2008-12-01T09:15:00Z',
            '2008-11-01T09:15:00Z',
            '2008-10-01T08:15:00Z',
            '2008-09-01T08:15:00Z',
            '2008-08-01T08:15:00Z',
            '2008-07-01T08:15:00Z',
            '2008-06-01T08:15:00Z',
            '2008-05-01T08:15:00Z',
            '2008-04-01T08:15:00Z',
            '2008-03-01T09:15:00Z',
            '2008-02-01T09:15:00Z',
            '2008-01-01T09:15:00Z',
            '2007-12-01T09:15:00Z',
            '2007-11-01T09:15:00Z',
            '2007-10-01T08:15:00Z',
            '2007-09-01T08:15:00Z',
            '2007-08-01T08:15:00Z',
            '2007-07-01T08:15:00Z',
            '2007-06-01T08:15:00Z',
            '2007-05-01T08:15:00Z',
            '2007-04-01T08:15:00Z',
            '2007-03-01T09:15:00Z',
            '2007-02-01T09:15:00Z',
            '2007-01-01T09:15:00Z',
            '2006-12-01T09:15:00Z',
            '2006-11-01T09:15:00Z',
            '2006-10-01T08:15:00Z',
            '2006-09-01T08:15:00Z',
            '2006-08-01T08:15:00Z',
            '2006-07-01T08:15:00Z',
            '2006-06-01T08:15:00Z',
            '2006-05-01T08:15:00Z',
            '2006-04-01T08:15:00Z',
            '2006-03-01T09:15:00Z',
            '2006-02-01T09:15:00Z',
            '2006-01-01T09:15:00Z',
            '2005-12-01T09:15:00Z',
            '2005-11-01T09:15:00Z',
            '2005-10-01T08:15:00Z',
            '2005-09-01T08:15:00Z',
            '2005-08-01T08:15:00Z',
            '2005-07-01T08:15:00Z',
            '2005-06-01T08:15:00Z',
            '2005-05-01T08:15:00Z',
            '2005-04-01T08:15:00Z',
            '2005-03-01T09:15:00Z',
            '2005-02-01T09:15:00Z',
            '2005-01-01T09:15:00Z',
            '2004-12-01T09:15:00Z',
            '2004-11-01T09:15:00Z',
            '2004-10-01T08:15:00Z',
            '2004-09-01T08:15:00Z',
            '2004-08-01T08:15:00Z',
            '2004-07-01T08:15:00Z',
            '2004-06-01T08:15:00Z',
            '2004-05-01T08:15:00Z',
            '2004-04-01T08:15:00Z',
            '2004-03-01T09:15:00Z',
            '2004-02-01T09:15:00Z',
            '2004-01-01T09:15:00Z',
            '2003-12-01T09:15:00Z',
            '2003-11-01T09:15:00Z',
            '2003-10-01T08:15:00Z',
            '2003-09-01T08:15:00Z',
            '2003-08-01T08:15:00Z',
            '2003-07-01T08:15:00Z',
            '2003-06-01T08:15:00Z',
            '2003-05-01T08:15:00Z',
            '2003-04-01T08:15:00Z',
            '2003-03-01T09:15:00Z',
            '2003-02-01T09:15:00Z',
            '2003-01-01T09:15:00Z',
            '2002-12-01T09:15:00Z',
            '2002-11-01T09:15:00Z',
            '2002-10-01T08:15:00Z',
            '2002-09-01T08:15:00Z',
            '2002-08-01T08:15:00Z',
            '2002-07-01T08:15:00Z',
            '2002-06-01T08:15:00Z',
            '2002-05-01T08:15:00Z',
            '2002-04-01T08:15:00Z',
            '2002-03-01T09:15:00Z',
            '2002-02-01T09:15:00Z',
            '2002-01-01T09:15:00Z',
            '2001-12-01T09:15:00Z',
            '2001-11-01T09:15:00Z',
            '2001-10-01T08:15:00Z',
            '2001-09-01T08:15:00Z',
            '2001-08-01T08:15:00Z',
            '2001-07-01T08:15:00Z',
            '2001-06-01T08:15:00Z',
            '2001-05-01T08:15:00Z',
            '2001-04-01T08:15:00Z',
            '2001-03-01T09:15:00Z',
            '2001-02-01T09:15:00Z',
            '2001-01-01T09:15:00Z',
            '2000-12-01T09:15:00Z',
            '2000-11-01T09:15:00Z',
            '2000-10-01T08:15:00Z',
            '2000-09-01T08:15:00Z',
            '2000-08-01T08:15:00Z',
            '2000-07-01T08:15:00Z',
            '2000-06-01T08:15:00Z',
            '2000-05-01T08:15:00Z',
            '2000-04-01T08:15:00Z',
            '2000-03-01T09:15:00Z',
            '2000-02-01T09:15:00Z',
            '2000-01-01T09:15:00Z',
            '1999-12-01T09:15:00Z',
            '1999-11-01T09:15:00Z',
            '1999-10-01T08:15:00Z',
            '1999-09-01T08:15:00Z',
            '1999-08-01T08:15:00Z',
            '1999-07-01T08:15:00Z',
            '1999-06-01T08:15:00Z',
            '1999-05-01T08:15:00Z',
            '1999-04-01T08:15:00Z',
            '1999-03-01T09:15:00Z',
            '1999-02-01T09:15:00Z',
            '1999-01-01T09:15:00Z'
        ],
        valores: [
            2.804, 2.747, 2.565, 2.221, 2.245, 2.267, 2.217, 2.187, 2.172, 2.114, 2.079, 2.081,
            2.081, 2.143, 2.398, 2.407, 2.525, 2.436, 2.506, 2.691, 2.936, 3.166, 3.526, 3.65, 3.68,
            3.703, 3.718, 3.671, 3.609, 3.679, 4.022, 4.16, 4.149, 4.073, 4.149, 4.007, 3.862,
            3.757, 3.647, 3.534, 3.337, 3.018, 2.828, 2.629, 2.233, 1.249, 0.992, 0.852, 0.287,
            0.013, -0.237, -0.335, -0.477, -0.502, -0.487, -0.477, -0.492, -0.498, -0.491, -0.484,
            -0.481, -0.484, -0.487, -0.501, -0.505, -0.497, -0.481, -0.466, -0.415, -0.359, -0.279,
            -0.147, -0.081, -0.108, -0.266, -0.288, -0.253, -0.261, -0.272, -0.304, -0.339, -0.356,
            -0.283, -0.19, -0.134, -0.112, -0.109, -0.108, -0.116, -0.129, -0.147, -0.154, -0.166,
            -0.169, -0.18, -0.181, -0.188, -0.19, -0.191, -0.191, -0.189, -0.19, -0.189, -0.18,
            -0.168, -0.156, -0.154, -0.149, -0.127, -0.119, -0.11, -0.106, -0.095, -0.08, -0.074,
            -0.069, -0.057, -0.048, -0.056, -0.028, -0.013, -0.01, -0.012, -0.008, 0.042, 0.059,
            0.079, 0.128, 0.154, 0.161, 0.167, 0.163, 0.165, 0.18, 0.212, 0.255, 0.298, 0.329,
            0.335, 0.338, 0.362, 0.469, 0.488, 0.513, 0.592, 0.604, 0.577, 0.549, 0.562, 0.543,
            0.506, 0.541, 0.543, 0.542, 0.525, 0.507, 0.484, 0.528, 0.545, 0.594, 0.575, 0.549,
            0.588, 0.65, 0.74, 0.877, 1.061, 1.219, 1.266, 1.368, 1.499, 1.678, 1.837, 2.004, 2.044,
            2.11, 2.067, 2.097, 2.183, 2.144, 2.147, 2.086, 1.924, 1.714, 1.55, 1.526, 1.54, 1.495,
            1.42, 1.421, 1.373, 1.281, 1.249, 1.225, 1.215, 1.225, 1.232, 1.242, 1.231, 1.243,
            1.261, 1.334, 1.412, 1.61, 1.644, 1.771, 1.909, 2.135, 2.622, 3.452, 4.35, 5.248, 5.384,
            5.323, 5.393, 5.361, 4.994, 4.82, 4.59, 4.349, 4.498, 4.793, 4.607, 4.647, 4.724, 4.666,
            4.564, 4.505, 4.373, 4.253, 4.106, 4.094, 4.064, 3.921, 3.864, 3.799, 3.715, 3.615,
            3.539, 3.401, 3.308, 3.221, 3.105, 2.914, 2.833, 2.783, 2.684, 2.414, 2.219, 2.223,
            2.168, 2.103, 2.193, 2.265, 2.335, 2.31, 2.312, 2.301, 2.328, 2.316, 2.377, 2.302,
            2.361, 2.404, 2.297, 2.163, 2.055, 2.163, 2.216, 2.381, 2.41, 2.303, 2.258, 2.279,
            2.076, 2.014, 2.252, 2.447, 2.411, 2.504, 2.705, 2.873, 3.017, 3.126, 3.236, 3.44,
            3.645, 3.869, 3.963, 3.86, 3.816, 3.594, 3.483, 3.298, 3.198, 3.369, 3.77, 4.108, 4.311,
            4.312, 4.52, 4.481, 4.471, 4.591, 4.574, 4.881, 5.193, 5.218, 5.219, 5.248, 5.105,
            4.965, 4.849, 4.365, 4.267, 4.111, 3.949, 3.826, 3.689, 3.684, 3.301, 3.237, 3.03,
            2.836, 2.683, 2.756, 3.046, 3.03, 3.062
        ]
    }
]
